import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';

/**
 * Hook to fetch the list of pubkeys that the current user follows
 * Uses NIP-02 Contact List (kind 3) events
 */
export function useFollowing() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['following', user?.pubkey],
    queryFn: async () => {
      if (!user?.pubkey) return [];
      
      // Query kind 3 (Contact List) for the current user
      const events = await nostr.query([{
        kinds: [3],
        authors: [user.pubkey],
        limit: 1,
      }]);

      if (events.length === 0) return [];

      // Get the most recent contact list
      const contactList = events[0];
      
      // Extract pubkeys from "p" tags
      const followingPubkeys = contactList.tags
        .filter(([name]) => name === 'p')
        .map(([_, pubkey]) => pubkey)
        .filter((pubkey): pubkey is string => !!pubkey);

      return followingPubkeys;
    },
    enabled: !!user?.pubkey,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to check if the current user follows a specific pubkey
 */
export function useIsFollowing(pubkey: string) {
  const { data: following } = useFollowing();
  return following?.includes(pubkey) ?? false;
}
